import { useEffect, useState } from "react";
import { LazyConfetti } from "@/components/ui/lazy-confetti";
import { useWindowSize } from "@/hooks/use-window-size";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Phone, MessageSquare, Users, Zap } from "lucide-react";

export type MilestoneType = "first_sms_sent" | "first_call_made" | "contacts_10_added" | "ai_composer_used";

interface MilestoneConfig {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta?: {
    text: string;
    action: () => void;
  };
}

const milestoneConfigs: Record<MilestoneType, MilestoneConfig> = {
  first_sms_sent: {
    icon: <MessageSquare className="w-16 h-16 text-primary" />,
    title: "🎉 Congrats! Your First Message Sent!",
    description: "You've successfully sent your first SMS with LimePhone. Ready to automate your messaging with AI?",
    cta: {
      text: "Try AI Message Composer",
      action: () => {
        // Will be set by the hook consumer
      }
    }
  },
  first_call_made: {
    icon: <Phone className="w-16 h-16 text-primary" />,
    title: "👏 Congrats! You've made your first LimePhone call.",
    description: "Ready to connect your AI agent to handle calls automatically?",
    cta: {
      text: "Set Up AI Voice Agent",
      action: () => {
        // Will be set by the hook consumer
      }
    }
  },
  contacts_10_added: {
    icon: <Users className="w-16 h-16 text-primary" />,
    title: "🎊 Amazing! You've added 10 contacts!",
    description: "Your contact list is growing! Now you can send bulk messages or set up targeted campaigns.",
    cta: {
      text: "Create Your First Campaign",
      action: () => {
        // Will be set by the hook consumer
      }
    }
  },
  ai_composer_used: {
    icon: <Zap className="w-16 h-16 text-primary" />,
    title: "✨ Welcome to AI-Powered Messaging!",
    description: "You've unlocked the power of AI! Your messages will never be the same.",
  }
};

interface MilestoneCelebrationProps {
  milestone: MilestoneType | null;
  onClose: () => void;
  onCtaClick?: (milestone: MilestoneType) => void;
}

export function MilestoneCelebration({ milestone, onClose, onCtaClick }: MilestoneCelebrationProps) {
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (milestone) {
      setShowConfetti(true);
      // Stop confetti after 5 seconds
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [milestone]);

  if (!milestone) return null;

  const config = milestoneConfigs[milestone];

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick(milestone);
    }
    onClose();
  };

  return (
    <>
      {showConfetti && (
        <LazyConfetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
        />
      )}
      
      <Dialog open={!!milestone} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              {config.icon}
            </div>
            <DialogTitle className="text-center text-2xl font-bold">
              {config.title}
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              {config.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-3 mt-4">
            {config.cta && (
              <Button
                onClick={handleCtaClick}
                className="w-full"
                size="lg"
                data-testid="button-milestone-cta"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {config.cta.text}
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full"
              data-testid="button-milestone-dismiss"
            >
              Continue Exploring
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
